import { isRunningInAppWebView } from 'common/components/AppModeDetector';
import { theme } from 'common/theme/app';
import AppAwareLink from 'components/BlogSidebar/AppAwareLink';
import Seo from 'components/seo';
import ContentPage from 'layouts/ContentPage';
import SiteLayout from 'layouts/SiteLayout';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

const TopicGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin: 8px 0 32px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const TopicCard = styled.a`
  display: block;
  padding: 16px 18px;
  border-radius: 14px;
  border: 1px solid ${theme.colors.borderColor};
  background: ${theme.colors.surfaceMuted || '#faf7f2'};
  color: ${theme.colors.headingColor} !important;
  text-decoration: none !important;
  font-weight: 700;
  line-height: 1.35;
  transition: border-color 0.2s ease, background 0.2s ease;

  span {
    display: block;
    margin-top: 6px;
    color: ${theme.colors.textColorAlt};
    font-size: 14px;
    font-weight: 500;
  }

  &:hover {
    border-color: ${theme.colors.primary};
    background: #fff;
  }

  &:focus-visible {
    outline: 3px solid ${theme.colors.focusRing};
    outline-offset: 2px;
  }
`;

const Callout = styled.div`
  margin: 28px 0;
  padding: 18px 20px;
  border-radius: 14px;
  border: 1px solid ${theme.colors.borderColor};
  background: ${theme.colors.cream || '#f7f1e8'};

  p {
    margin-bottom: 0;
  }
`;

const topics = [
  {
    id: 'getting-started',
    title: 'Getting started',
    blurb: 'Sign in, add pets, and set up care basics',
  },
  {
    id: 'home-care',
    title: 'Home, Schedule & Diet',
    blurb: 'Track weight, reminders, meals, and progress',
  },
  {
    id: 'connect',
    title: 'Connect',
    blurb: 'Stories, Q&A, Lost & Found, and messaging',
  },
  {
    id: 'services-marketplace',
    title: 'Services & Marketplace',
    blurb: 'Nearby places, listings, and promotions',
  },
  {
    id: 'saved-drafts',
    title: 'Saved posts & drafts',
    blurb: 'Bookmark stories and finish drafts later',
  },
  {
    id: 'subscriptions',
    title: 'Plans & subscriptions',
    blurb: 'Free vs Pro and how to manage billing',
  },
  {
    id: 'privacy-account',
    title: 'Privacy & account',
    blurb: 'Location, blocking, and account deletion',
  },
  {
    id: 'troubleshooting',
    title: 'Troubleshooting',
    blurb: 'Common fixes when something feels off',
  },
];

const Help = () => {
  const [hideChrome, setHideChrome] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const params = new URLSearchParams(window.location.search);
    if (params.get('app') === 'true' || isRunningInAppWebView()) {
      setHideChrome(true);
    }
  }, []);

  return (
    <SiteLayout showFooter={!hideChrome} showNavbar={!hideChrome}>
      <Seo
        title="Help Center | Furbuz Pet Care App"
        description="Guides for Furbuz features: pets, reminders, Connect, nearby services, marketplace, saved posts, Pro subscription, privacy, and troubleshooting."
      />
      <ContentPage
        title="Help Center"
        description="Quick guides for everyday Furbuz features. Update-friendly docs you can open anytime from Settings."
        eyebrow="Support"
      >
        <p>
          Use this Help Center to learn how Furbuz works. Prefer short answers?
          Visit the <AppAwareLink to="/faq">FAQ</AppAwareLink>. Need a person?{' '}
          <AppAwareLink to="/contact">Contact us</AppAwareLink> or send feedback
          from the app.
        </p>

        <TopicGrid>
          {topics.map((topic) => (
            <TopicCard key={topic.id} href={`#${topic.id}`}>
              {topic.title}
              <span>{topic.blurb}</span>
            </TopicCard>
          ))}
        </TopicGrid>

        <h2 id="getting-started">Getting started</h2>
        <ol>
          <li>Sign in with Apple, Google, or email.</li>
          <li>Create your profile, then add your first pet.</li>
          <li>
            Grant notification permission so reminders can reach you on time.
          </li>
          <li>
            Optional: allow location for nearby Lost &amp; Found and pet
            services.
          </li>
        </ol>
        <p>
          Free includes core care tracking for up to two pets. Upgrade to Furbuz
          Pro anytime for unlimited pets, ad-free Connect, and advanced reports.
        </p>

        <h2 id="home-care">Home, Schedule &amp; Diet</h2>
        <h3>Home</h3>
        <p>
          Home shows weight trends, today’s diet plan, and pet articles. Swipe
          between pets when you have more than one. Use <strong>See All</strong>{' '}
          to open full history or lists.
        </p>
        <h3>Schedule</h3>
        <p>
          Add one-time or repeating reminders (meds, walks, vet visits, grooming).
          Open a reminder for details, edits, or completion history.
        </p>
        <h3>Diet &amp; Progress</h3>
        <ul>
          <li>Log meals, portions, and supplements per pet.</li>
          <li>Record weight and other progress entries over time.</li>
          <li>Pro unlocks deeper analytics and exportable reports.</li>
        </ul>

        <h2 id="connect">Connect</h2>
        <p>
          Connect is the community hub. Tabs include Stories, Marketplace /
          Buy &amp; Sell, Q&amp;A, Services, and Lost &amp; Found.
        </p>
        <ul>
          <li>
            <strong>Stories:</strong> share photos or updates; like, comment,
            repost, share, or save posts.
          </li>
          <li>
            <strong>Q&amp;A:</strong> ask pet-care questions by category and vote
            on helpful answers.
          </li>
          <li>
            <strong>Lost &amp; Found:</strong> post alerts with details and
            location when possible.
          </li>
          <li>
            <strong>Inbox:</strong> message other pet parents from Connect.
          </li>
        </ul>
        <p>
          Keep posts respectful and pet-related. See our{' '}
          <AppAwareLink to="/community-guidelines">
            Community Guidelines
          </AppAwareLink>
          .
        </p>

        <h2 id="services-marketplace">Services &amp; Marketplace</h2>
        <h3>Nearby pet services</h3>
        <p>
          With location on, Services shows nearby vets, groomers, parks, shops,
          and boarding. Search and filter by category. Promoted businesses may
          appear with a badge.
        </p>
        <h3>Claim &amp; promote</h3>
        <p>
          Business owners can claim a place and purchase Sponsored or Featured
          placement through Stripe Checkout. After payment, the listing stays
          active for the subscription period.
        </p>
        <h3>Marketplace</h3>
        <p>
          Buy &amp; Sell listings let pet parents post items. Follow listing
          rules and use in-app messaging to coordinate safely. Furbuz does not
          process marketplace payments.
        </p>

        <h2 id="saved-drafts">Saved posts &amp; drafts</h2>
        <p>
          On Connect, open the menu (three lines) for:
        </p>
        <ul>
          <li>
            <strong>Drafts:</strong> finish or publish posts you saved for later.
          </li>
          <li>
            <strong>Saved posts:</strong> bookmarks you saved with the ribbon
            icon on a story.
          </li>
        </ul>
        <p>Tap the bookmark again to remove a post from Saved.</p>

        <h2 id="subscriptions">Plans &amp; subscriptions</h2>
        <ul>
          <li>
            <strong>Free:</strong> up to two pets, full care basics, Connect with
            light ads.
          </li>
          <li>
            <strong>Furbuz Pro:</strong> unlimited pets, no ads, advanced
            analytics, CSV/PDF reports.
          </li>
        </ul>
        <p>
          Subscribe or restore purchases in Settings → Current Plan. Manage or
          cancel in the App Store or Google Play account that billed you. More
          detail is on the <AppAwareLink to="/faq">FAQ</AppAwareLink>.
        </p>

        <h2 id="privacy-account">Privacy &amp; account</h2>
        <ul>
          <li>
            Location is optional and used for nearby services and Lost &amp;
            Found.
          </li>
          <li>
            Block users from Settings → Blocked users if you need distance.
          </li>
          <li>
            Delete your account from Settings. See{' '}
            <AppAwareLink to="/account-deletion">
              account deletion
            </AppAwareLink>{' '}
            for timing and data removal.
          </li>
        </ul>
        <p>
          Read the full{' '}
          <AppAwareLink to="/privacy-policy">Privacy Policy</AppAwareLink> and{' '}
          <AppAwareLink to="/terms-and-conditions">Terms</AppAwareLink>.
        </p>

        <h2 id="troubleshooting">Troubleshooting</h2>
        <ul>
          <li>
            <strong>Sign-in fails / host lookup:</strong> check Wi‑Fi or mobile
            data, turn off Private DNS or VPN, then retry.
          </li>
          <li>
            <strong>No nearby services:</strong> enable location for Furbuz and
            pull to refresh Services.
          </li>
          <li>
            <strong>Reminders silent:</strong> allow notifications for Furbuz in
            system settings.
          </li>
          <li>
            <strong>Checkout or promote issues:</strong> finish payment in the
            browser, then return to the app; check your email receipt from
            Stripe.
          </li>
          <li>
            <strong>Content under review:</strong> some posts are held briefly
            for safety checks before they appear publicly.
          </li>
        </ul>

        <Callout>
          <p>
            Still stuck? Send feedback from{' '}
            <strong>Settings → Send Feedback</strong> in the app, or{' '}
            <AppAwareLink to="/contact">contact support</AppAwareLink>. Include
            your account email and what you were trying to do.
          </p>
        </Callout>
      </ContentPage>
    </SiteLayout>
  );
};

export default Help;
